import { Platform } from "react-native";
import * as ExpoImagePicker from "expo-image-picker";
import ImagePicker from "react-native-image-crop-picker";

import { UserImageType } from "@/schemas/user/image";

const IMAGE_MAX_LONG_SIDE = 1620;
const IMAGE_AVATAR_SIZE = 300;

type Props = {
  imageType: UserImageType;
  aspectRatio: {
    width: number;
    height: number;
  };
  compressionQuality: number;
  onPickerCancel?: () => void;
};

export const universalImagePicker = async ({
  imageType,
  aspectRatio,
  compressionQuality,
  onPickerCancel,
}: Props): Promise<string[] | null> => {
  if (Platform.OS === "web") {
    return null;
  }

  const quality =
    compressionQuality >= 0 && compressionQuality <= 1
      ? compressionQuality
      : 0.8;

  const getCropDimensions = (
    imageType: UserImageType,
    aspectRatio: {
      width: number;
      height: number;
    },
  ): { width: number; height: number } => {
    if (imageType === UserImageType.AVATAR) {
      return { width: IMAGE_AVATAR_SIZE, height: IMAGE_AVATAR_SIZE };
    }
    // Fit within IMAGE_MAX_LONG_SIDE, preserving aspect ratio
    if (aspectRatio.width >= aspectRatio.height) {
      const width = IMAGE_MAX_LONG_SIDE;
      const height = Math.round(
        (IMAGE_MAX_LONG_SIDE * aspectRatio.height) / aspectRatio.width,
      );
      return { width, height };
    } else {
      const height = IMAGE_MAX_LONG_SIDE;
      const width = Math.round(
        (IMAGE_MAX_LONG_SIDE * aspectRatio.width) / aspectRatio.height,
      );
      return { width, height };
    }
  };

  if (Platform.OS === "ios") {
    // iOS - use react-native-image-crop-picker
    // since expo-image-picker only support 1:1 aspect ratio cropping
    const { width, height } = getCropDimensions(imageType, aspectRatio);

    try {
      const pickerResult = await ImagePicker.openPicker({
        width: width,
        height: height,
        cropping: true,
        mediaType: "photo",
        // multiple: allowMultiple, // TODO: need to set to true when multiple selection is supported
        compressImageQuality: quality,
        cropperStatusBarColor: "#000000",
        cropperToolbarColor: "#000000",
        cropperActiveWidgetColor: "#007AFF",
        cropperToolbarTitle: "Edit Photo",
        loadingLabelText: "Processing your photo...",
        compressImageMaxWidth: width,
        compressImageMaxHeight: height,
      });

      if (pickerResult === null) {
        return null;
      }

      return [pickerResult.path];
    } catch (error) {
      if (
        error &&
        typeof error === "object" &&
        "code" in error &&
        error.code === "E_PICKER_CANCELLED"
      ) {
        // console.log("iOS: Image picker was cancelled");
        onPickerCancel?.();
        return null;
      }
      throw error;
    }
  } else {
    const pickerResult = await ExpoImagePicker.launchImageLibraryAsync({
      allowsEditing: true,
      allowsMultipleSelection: false, // TODO: need to set to true when multiple selection is supported
      mediaTypes: ["images"],
      aspect: [aspectRatio.width, aspectRatio.height],
      quality: quality,
    });

    if (pickerResult.canceled) {
      // console.log("Android: Image picker was cancelled");
      onPickerCancel?.();
      return null;
    }
    return pickerResult.assets.map((asset) => asset.uri);
  }
};
